import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {CodeReviewService} from './code-review.service';
import {
  AiActionType,
  CodeReviewTabWebSocketFromClient,
  CodeReviewTabWebSocketToClient,
  GitLabCommitOverviewDto,
  GitlabDiscussion,
  GitlabDiscussionNote,
  GitlabMergeRequest, GitlabMrAlertIgnoreDto,
  GitlabMrAlertIgnoreReason,
  GitlabOpenMergeRequests,
  GitLabProjectOverviewDto,
  MergeMrsResultDto,
} from './code-review-models';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription, from, mergeMap, map} from 'rxjs';
import {WsAbstraction, WsService} from '../../services/ws.service';
import { NgClass, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QwenCommandListComponent } from '../qwen-command-list/qwen-command-list.component';
import { QwenCommandDto } from '../qwen/qwen.service';
import { AiCommandListComponent } from '../ai-command-list/ai-command-list.component';
import { AiCommandDto } from '../ai/ai.service';

@Component({
    selector: 'app-code-review',
    templateUrl: './code-review.component.html',
    styleUrls: ['./code-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgClass, CommonModule, QwenCommandListComponent, AiCommandListComponent]
})
export class CodeReviewComponent implements OnInit, OnDestroy {

  public displayOnlyNonAccepted = true;
  public autoAdvance = true;
  public refreshing = false;
  public merging = false;

  public showAdvancedOpenMrs = true;
  public showCompletelyOpenMrs = true;
  public showOpenWithoutReason = false;
  public showOpenWithReason = false;
  public shownReasons: number[] = [];
  public showNotMine = false;
  public showIgnoredButNoLongerOpen = false;
  public showMarkedAsNotMineButNoOpenMRs = false;

  public selectedReason: number;

  projects: GitLabProjectOverviewDto[] = [];
  public commits: GitLabCommitOverviewDto[] = [];
  public selectedProject: number;

  public selectedCommits: number[] = [];

  public selectedMrUrls = new Set<string>();
  private lastClickedMrIndex: number | null = null;

  public mergeRequestsAdvanced: GitlabOpenMergeRequests = {
    notMineConfig: [],
    completelyOpen: [],
    openButNotMine: [],
    openIgnoredWithoutReason: [],
    openIgnoredWithReason: [],
    ignoredButNoLongerOpen: [],
    projectNotMineEntriesNotActuallyIgnoringAnything: [],
  };

  public aiActionCounts: {[key: string]: number} = {};
  public expandedAiActionsMrUrl: string | null = null;
  public expandedDiscussionsMrUrl: string | null = null;
  public expandedQwenCommands: QwenCommandDto[] = [];
  public expandedAiCommands: AiCommandDto[] = [];

  private subscriptions: Subscription = new Subscription();
  private wsAbstraction: WsAbstraction<CodeReviewTabWebSocketFromClient, CodeReviewTabWebSocketToClient>;

  constructor(
    private codeReviewService: CodeReviewService,
    private notificationService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private wsService: WsService,
  ) { }

  ngOnInit(): void {
    this.wsAbstraction = this.wsService.setUpWs<CodeReviewTabWebSocketFromClient, CodeReviewTabWebSocketToClient>('/wsocket/codereview');
    this.subscriptions.add(
      this.wsAbstraction.read().subscribe((res: CodeReviewTabWebSocketToClient) => {
        console.info('per ws received', res);
        this.loadProjectData();
        if (this.selectedProject) {
          this.loadCommits();
        }
        this.loadFoundMrRequest();
        if (this.expandedAiActionsMrUrl) {
          this.refreshExpandedAiActions();
        }
      }));

    this.loadProjectData();
    this.subscriptions.add(
      this.activatedRoute.queryParamMap.subscribe(queryParamMap => {
        this.commits = [];
        this.selectedProject = null;
        this.selectedCommits = [];
        if (queryParamMap.get('projectId')) {
          this.selectedProject = +queryParamMap.get('projectId');
          this.loadCommits();
        } else {
          this.selectedProject = null;
        }
      }));

    this.loadFoundMrRequest();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public onSettingChanged() {
    this.loadProjectData();
    this.loadCommits();
  }

  private loadCommits() {
    this.codeReviewService.getCommitsPerProject(this.selectedProject, this.displayOnlyNonAccepted).subscribe({
      next: res => {
        this.commits = res;
        if (this.autoAdvance) {
          this.selectedCommits = this.commits.map(commit => commit.strapiId);
        }
      },
      error: err => this.notificationService.error('failed to load data: ' + JSON.stringify(err)),
    });
  }

  loadProjectData() {
    this.codeReviewService.getGitlabReviewProjectsOverview(this.displayOnlyNonAccepted).subscribe({
      next: res => {
        this.projects = res;
      },
      error: err => {
        this.notificationService.error('failed to load data: ' + JSON.stringify(err));
      }
    });
  }

  public getProjectView(): GitLabProjectOverviewDto[] {
    return this.projects.filter(proj => !this.displayOnlyNonAccepted || proj.nonAcceptedCommits > 0);
  }

  public select(proj: GitLabProjectOverviewDto) {
    console.info('navigating to ', proj);
    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams: {projectId: proj.strapiProjectId},
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });

  }

  setAccepted() {
    let nextProjectToSelect: GitLabProjectOverviewDto = null;
    if (this.autoAdvance) {
      const projects = this.getProjectView();
      const currentIndex = projects.findIndex(proj => proj.strapiProjectId === this.selectedProject);
      const nextIndex = currentIndex + 1;
      if (nextIndex < projects.length) {
        nextProjectToSelect = projects[nextIndex];
      }
    }
    this.codeReviewService.setManuallyAccepted(this.selectedCommits).subscribe({
      next: res => {
        this.loadProjectData();
        if (!nextProjectToSelect) {
          this.loadCommits();
        }
      },
      error: err => {
        this.notificationService.error('failed to set acceptance: ' + JSON.stringify(err));
      }
    });

    if (this.autoAdvance) {
      if (nextProjectToSelect) {
        this.select(nextProjectToSelect);
      } else {
        this.loadCommits();
      }
    }
  }

  toggleAllSelectedCommits() {
    if (this.selectedCommits.length) {
      this.selectedCommits = [];
    } else {
      this.selectedCommits = this.commits.map(commit => commit.strapiId);
    }
  }

  getCommitUrl(hash: string): string {
    const proj = this.projects.find(p => p.strapiProjectId === this.selectedProject);
    if (!proj) {
      return '';
    }
    return `${proj.url}/-/commit/${hash}`;
  }

  toggleSelectedCommit(commitId: number) {
    if (this.selectedCommits.includes(commitId)) {
      this.selectedCommits = this.selectedCommits.filter((c: number) => c !== commitId);
    } else {
      this.selectedCommits.push(commitId);
    }
  }

  private loadFoundMrRequest() {
    this.codeReviewService.getMergeRequestsAdvanced().subscribe({
      next: res => {
        this.mergeRequestsAdvanced = res;
        this.pruneMrSelection();
        this.loadAiActionCounts();
      },
      error: err => {
        this.notificationService.error('failed to load data');
        console.error('failed to load data', err);
      }
    });
  }

  loadAiActionCounts() {
    const allMrs = [
      ...(this.showCompletelyOpenMrs ? this.mergeRequestsAdvanced.completelyOpen : []),
      ...(this.showNotMine ? this.mergeRequestsAdvanced.openButNotMine : []),
      ...(this.showOpenWithoutReason ? this.mergeRequestsAdvanced.openIgnoredWithoutReason : []),
      ...this.mergeRequestsAdvanced.openIgnoredWithReason
        .filter(reason => this.shownReasons.includes(reason.id))
        .flatMap(reason => reason.ignoredMrs)
    ];

    if (allMrs.length === 0) {
      return;
    }

    from(allMrs).pipe(
      mergeMap(mrInfo => {
        return this.codeReviewService.findAiActionsForMr(mrInfo.mr.web_url).pipe(
          map(actions => {
            this.aiActionCounts[mrInfo.mr.web_url] = actions.length;
            return actions;
          })
        );
      }, 5) // max 5 concurrent calls
    ).subscribe();
  }

  expandAiActions(mrWebUrl: string) {
    if (this.expandedAiActionsMrUrl === mrWebUrl) {
      this.expandedAiActionsMrUrl = null;
      this.expandedQwenCommands = [];
      this.expandedAiCommands = [];
      return;
    }

    this.expandedAiActionsMrUrl = mrWebUrl;
    this.refreshExpandedAiActions();
  }

  expandDiscussions(mrWebUrl: string) {
    this.expandedDiscussionsMrUrl = this.expandedDiscussionsMrUrl === mrWebUrl ? null : mrWebUrl;
  }

  /**
   * The open discussion threads are shipped with the merge requests themselves, so they are always read from the
   * current data instead of being copied on expand - otherwise they would go stale on the next websocket refresh.
   */
  get expandedDiscussionThreads(): Array<GitlabDiscussion> {
    if (!this.expandedDiscussionsMrUrl) {
      return [];
    }

    return this.findMrByWebUrl(this.expandedDiscussionsMrUrl)?.openDiscussionThreads ?? [];
  }

  private findMrByWebUrl(mrWebUrl: string): GitlabMergeRequest | null {
    const allMrs = [
      ...this.mergeRequestsAdvanced.completelyOpen,
      ...this.mergeRequestsAdvanced.openButNotMine,
      ...this.mergeRequestsAdvanced.openIgnoredWithoutReason,
      ...this.mergeRequestsAdvanced.openIgnoredWithReason.flatMap(reason => reason.ignoredMrs)
    ];

    return allMrs.find(mrInfo => mrInfo.mr.web_url === mrWebUrl)?.mr ?? null;
  }

  getDiscussionNoteLocation(note: GitlabDiscussionNote): string {
    if (!note.position) {
      return '';
    }

    const path = note.position.new_path || note.position.old_path;
    const line = note.position.new_line ?? note.position.old_line;
    return line ? `${path}:${line}` : (path ?? '');
  }

  getDiscussionNoteLink(note: GitlabDiscussionNote): string {
    return `${this.expandedDiscussionsMrUrl}#note_${note.id}`;
  }

  private refreshExpandedAiActions() {
    if (!this.expandedAiActionsMrUrl) {
      return;
    }

    this.codeReviewService.findAiActionsForMr(this.expandedAiActionsMrUrl).subscribe({
      next: actions => {
        const uids = actions.map(a => a.stracliCmdUid).filter(uid => !!uid) as string[];
        if (uids.length > 0) {
          this.codeReviewService.getQwenCommandsForStracliCmdUids(uids).subscribe({
            next: commands => {
              this.expandedQwenCommands = commands;
            },
            error: err => this.notificationService.error('failed to load Qwen commands: ' + err.message)
          });
          this.codeReviewService.getAiCommandsForStracliCmdUids(uids).subscribe({
            next: commands => {
              this.expandedAiCommands = commands;
            },
            error: err => this.notificationService.error('failed to load AI commands: ' + err.message)
          });
        } else {
          this.expandedQwenCommands = [];
          this.expandedAiCommands = [];
        }
      },
      error: err => this.notificationService.error('failed to load AI actions: ' + err.message)
    });
  }

  onQwenCommandDeleted() {
    this.refreshExpandedAiActions();
    this.loadAiActionCounts();
  }

  onAiCommandDeleted() {
    this.refreshExpandedAiActions();
    this.loadAiActionCounts();
  }

  setMrRequestIgnore(mr: { web_url: string }, b: boolean, reasonId: number) {
    this.codeReviewService.setMrIgnore(mr, b, reasonId).subscribe(res => {
      this.loadFoundMrRequest();
    });
  }

  getClassesForMr(mr: GitlabMergeRequest): string[] {
    let ret: string[];
    const pipelineStatus = mr?.head_pipeline?.status;
    if (!pipelineStatus) {
      ret = ['pipeline-no-status'];
    } else if (pipelineStatus === 'failed') {
      ret = ['pipeline-error'];
    } else if (pipelineStatus === 'success') {
      ret = ['pipeline-success'];
    } else {
      ret = ['pipeline-unknown'];
    }

    if (mr.merge_status === 'cannot_be_merged') {
      ret.push('merge-conflict');
    }

    return ret;
  }

  getFlags(mr: GitlabMergeRequest): string {
    let ret = '';
    if (mr.merge_status === 'cannot_be_merged') {
      ret += 'MERGE-CONFLICT ';
    }
    return ret;
  }


  toggleShowReason(reason: GitlabMrAlertIgnoreReason) {
    const id = reason.id;
    const inIt = this.shownReasons.includes(id);
    if (inIt) {
      this.shownReasons = this.shownReasons.filter(it => it !== id);
    } else {
      this.shownReasons.push(id);
      this.loadAiActionCounts();
    }
  }

  getExpandIcon(shown: boolean) {
    if (shown) {
      return '⊖';
    } else {
      return '⊕';
    }
  }

  setActiveReason(mr: GitlabMergeRequest) {
    this.setMrRequestIgnore(mr, true, this.selectedReason);
  }

  setProjectNotMine(mr: GitlabMergeRequest, projectNotMine: boolean) {
    this.codeReviewService.setProjectNotMine({
      ignore: projectNotMine,
      mrUrl: mr.web_url,
      reasonId: null,
    }).subscribe({
      next: res => {
        this.loadFoundMrRequest();
      },
      error: err => {
        this.notificationService.error('failed to save');
        console.error('failed to save', err);
      },
    });
  }

  unignore(mr: GitlabMergeRequest) {
    this.setMrRequestIgnore(mr, false, null);
  }

  deleteObsoleteIgnore(mr: GitlabMrAlertIgnoreDto) {
    this.setMrRequestIgnore({web_url: mr.url}, false, null);
  }

  deleteProjectNotMine(id: number) {
    this.codeReviewService.deleteProjectNotMine(id).subscribe({
      next: () => this.loadFoundMrRequest(),
      error: err => this.notificationService.error('failed to delete: ' + JSON.stringify(err)),
    });
  }

  deleteAllObsolete() {
    this.mergeRequestsAdvanced.ignoredButNoLongerOpen.forEach(mr => {
      this.setMrRequestIgnore({web_url: mr.url}, false, null);
    });
  }

  deleteAllProjectNotMine() {
    this.mergeRequestsAdvanced.projectNotMineEntriesNotActuallyIgnoringAnything.forEach(entry => {
      this.codeReviewService.deleteProjectNotMine(entry.id).subscribe({
        next: () => this.loadFoundMrRequest(),
        error: err => this.notificationService.error('failed to delete: ' + JSON.stringify(err)),
      });
    });
  }

  triggerAiReview(mr: GitlabMergeRequest) {
    this.codeReviewService.triggerAiMrAction({
      type: AiActionType.REVIEW,
      mrWebUrl: mr.web_url
    }).subscribe({
      next: (res) => {
        this.notificationService.success('AI Review triggered');
        this.loadAiActionCounts();
      },
      error: (err) => this.notificationService.error(err.error?.message || err.message || JSON.stringify(err))
    });
  }

  triggerAiMockFix(mr: GitlabMergeRequest) {
    this.codeReviewService.triggerAiMrAction({
      type: AiActionType.FIX_MOCKS_DEPENDENCY_UPDATE,
      mrWebUrl: mr.web_url
    }).subscribe({
      next: (res) => {
        this.notificationService.success('AI Mock Fix triggered');
        this.loadAiActionCounts();
      },
      error: (err) => this.notificationService.error(err.error?.message || err.message || JSON.stringify(err))
    });
  }

  triggerAiFix(mr: GitlabMergeRequest) {
    this.codeReviewService.triggerAiMrAction({
      type: AiActionType.FIX,
      mrWebUrl: mr.web_url
    }).subscribe({
      next: (res) => {
        this.notificationService.success('AI Fix triggered');
        this.loadAiActionCounts();
      },
      error: (err) => this.notificationService.error(err.error?.message || err.message || JSON.stringify(err))
    });
  }

  triggerAiAnalyseFailure(mr: GitlabMergeRequest) {
    this.codeReviewService.triggerAiMrAction({
      type: AiActionType.ANALYSE_FAILURE,
      mrWebUrl: mr.web_url
    }).subscribe({
      next: (res) => {
        this.notificationService.success('AI Analyse Failure triggered');
        this.loadAiActionCounts();
      },
      error: (err) => this.notificationService.error(err.error?.message || err.message || JSON.stringify(err))
    });
  }

  triggerRefresh() {
    this.refreshing = true;
    this.codeReviewService.triggerRefresh().subscribe({
      next: () => {
        this.notificationService.success('MR list refresh triggered');
        this.refreshing = false;
        this.loadFoundMrRequest();
      },
      error: err => {
        this.notificationService.error('failed to trigger refresh: ' + JSON.stringify(err));
        this.refreshing = false;
      }
    });
  }

  onMrCheckboxClick(index: number, event: MouseEvent) {
    const mrs = this.mergeRequestsAdvanced.completelyOpen;
    const url = mrs[index].mr.web_url;
    // the click has already toggled the checkbox, so its checked state is the target state
    const select = (event.target as HTMLInputElement).checked;
    if (event.shiftKey && this.lastClickedMrIndex !== null && this.lastClickedMrIndex < mrs.length) {
      const rangeStart = Math.min(this.lastClickedMrIndex, index);
      const to = Math.max(this.lastClickedMrIndex, index);
      for (let i = rangeStart; i <= to; i++) {
        this.setMrSelected(mrs[i].mr.web_url, select);
      }
    } else {
      this.setMrSelected(url, select);
    }
    this.lastClickedMrIndex = index;
  }

  private setMrSelected(url: string, selected: boolean) {
    if (selected) {
      this.selectedMrUrls.add(url);
    } else {
      this.selectedMrUrls.delete(url);
    }
  }

  selectAllMrs() {
    this.selectedMrUrls = new Set(this.mergeRequestsAdvanced.completelyOpen.map(mr => mr.mr.web_url));
    this.lastClickedMrIndex = null;
  }

  selectNoMrs() {
    this.selectedMrUrls = new Set();
    this.lastClickedMrIndex = null;
  }

  private pruneMrSelection() {
    const openUrls = new Set(this.mergeRequestsAdvanced.completelyOpen.map(mr => mr.mr.web_url));
    this.selectedMrUrls = new Set([...this.selectedMrUrls].filter(url => openUrls.has(url)));
    this.lastClickedMrIndex = null;
  }

  mergeSelectedMrs() {
    const mrUrls = [...this.selectedMrUrls];
    if (!mrUrls.length || !confirm(`Merge ${mrUrls.length} MR(s)?`)) {
      return;
    }
    this.merging = true;
    this.codeReviewService.mergeMrs(mrUrls).subscribe({
      next: (res: MergeMrsResultDto) => {
        this.merging = false;
        if (res.mergedCount) {
          this.notificationService.success(`${res.mergedCount} MR(s) merged`);
        }
        if (res.scheduledCount) {
          this.notificationService.info(`${res.scheduledCount} MR(s) will be merged when their pipeline succeeds`);
        }
        res.results.filter(r => r.status === 'FAILED').forEach(r => {
          this.notificationService.error(r.message, `Merge failed: ${r.mrUrl}`, {disableTimeOut: true, closeButton: true});
        });
        res.results.filter(r => r.status !== 'FAILED').forEach(r => this.selectedMrUrls.delete(r.mrUrl));
        this.loadFoundMrRequest();
      },
      error: err => {
        this.merging = false;
        this.notificationService.error('failed to merge MRs: ' + (err.error?.message || err.message || JSON.stringify(err)));
      }
    });
  }
}
